import { supabase } from '../../lib/supabase';
import { ATLAS_BACKEND_URL } from '../../config';
import { fetchInChunks } from '../../utils/supabaseChunkedIn';
import { esBackendDePruebaPermitido } from './backendPermitido';
import type { Celda, Corrida, Evaluacion, TestSet } from './tipos';

/**
 * Persistencia del harness de test masivo — capa FINA sobre supabase-js.
 * Toda la lógica vive en celdas.ts / comparar.ts / ejecutar.ts (testeadas);
 * acá solo hay queries. Las 3 tablas tienen RLS por company
 * (id_company = get_my_company_id()), pero el INSERT igual lleva id_company
 * explícito porque la policy lo exige en el with check.
 */

export interface CompanyTest {
  idCompany: string;
  userId: string;
  devTester: boolean;
  imagenesRestantes: number;
  rubro: string | null;
  plan: string | null;
}

/** Company del usuario logueado + flag dev_tester (gate de la página) + créditos.
 *  Con el fallback legacy de perfiles viejos (company_id texto sin id_company),
 *  el mismo de ProductSelectorModal. Loguea el motivo cuando el gate rebota. */
export async function fetchCompanyTest(): Promise<CompanyTest | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    console.warn('[test-masivo] sin sesión');
    return null;
  }
  const { data: profile } = await supabase
    .from('profiles')
    .select('id_company, company_id')
    .eq('id', user.id)
    .single();
  let idCompany: string | null = (profile as { id_company?: string | null })?.id_company ?? null;
  const companyIdLegacy = (profile as { company_id?: string | null })?.company_id ?? null;
  if (!idCompany && companyIdLegacy) {
    const { data: comp } = await supabase
      .from('company_profiles')
      .select('id_company')
      .eq('id', companyIdLegacy)
      .single();
    idCompany = (comp as { id_company?: string })?.id_company ?? null;
  }
  if (!idCompany) {
    console.warn('[test-masivo] el perfil no tiene company asociada');
    return null;
  }
  const { data: company } = await supabase
    .from('company_profiles')
    .select('dev_tester, imagenes_restantes:imagenes_disponibles, rubro, plan')
    .eq('id_company', idCompany)
    .single();
  if (!company) {
    console.warn('[test-masivo] company_profiles sin fila para', idCompany);
    return null;
  }
  if (company.dev_tester !== true) {
    console.warn('[test-masivo] la cuenta NO es dev_tester → redirect a home');
  }
  return {
    idCompany,
    userId: user.id,
    devTester: company.dev_tester === true,
    imagenesRestantes: company.imagenes_restantes ?? 0,
    rubro: (company as { rubro?: string | null }).rubro ?? null,
    plan: (company as { plan?: string | null }).plan ?? null,
  };
}

export async function listarSets(): Promise<TestSet[]> {
  const { data, error } = await supabase
    .from('test_masivo_sets')
    .select('id, nombre, variante_ids, configs, created_at, updated_at')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TestSet[];
}

export async function guardarSet(
  idCompany: string,
  set: Omit<TestSet, 'id'> & { id?: string },
): Promise<TestSet> {
  const fila = {
    ...(set.id ? { id: set.id } : {}),
    id_company: idCompany,
    nombre: set.nombre,
    variante_ids: set.variante_ids,
    configs: set.configs,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('test_masivo_sets')
    .upsert(fila)
    .select('id, nombre, variante_ids, configs, created_at, updated_at')
    .single();
  if (error) throw error;
  return data as TestSet;
}

export async function listarCorridas(setId: string): Promise<Corrida[]> {
  const { data, error } = await supabase
    .from('test_masivo_corridas')
    .select('id, set_id, etiqueta, celdas, created_at, backend_url')
    .eq('set_id', setId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Corrida[];
}

export async function crearCorrida(
  idCompany: string,
  setId: string,
  etiqueta: string,
  celdas: Celda[],
  backendUrl: string | null = null,
): Promise<Corrida> {
  const { data, error } = await supabase
    .from('test_masivo_corridas')
    .insert({ id_company: idCompany, set_id: setId, etiqueta, celdas, backend_url: backendUrl })
    .select('id, set_id, etiqueta, celdas, created_at, backend_url')
    .single();
  if (error) throw error;
  return data as Corrida;
}

/**
 * POST de generación contra un backend ARBITRARIO (worktree local en otra versión
 * de git, otro puerto) — el mecanismo de comparación de VERSIONES DE PROMPT.
 * Mismo contrato y mismo JWT de Supabase que atlasBackendFetch; 200/202 son éxito
 * (la completitud la decide el poll por execution_id, nunca esta respuesta).
 */
export async function postGenerateContra(
  baseUrl: string,
  body: Record<string, unknown>,
): Promise<void> {
  // La barrera vive ACÁ, en la capa que adjunta el token, y no solo en la UI:
  // las corridas guardan su backend_url en la DB, así que una URL vieja o
  // manipulada re-entra por "Reanudar disparo" sin pasar por el input.
  if (!esBackendDePruebaPermitido(baseUrl, ATLAS_BACKEND_URL)) {
    throw new Error(
      `backend de test no permitido: ${baseUrl} — solo localhost o el backend propio (el JWT de la sesión viaja en el request)`,
    );
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Sin sesión para llamar al backend de test');
  const res = await fetch(`${baseUrl.replace(/\/+$/, '')}/v1/ads/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok && res.status !== 202) {
    const texto = await res.text().catch(() => '');
    throw new Error(`backend ${baseUrl} → HTTP ${res.status}: ${texto.slice(0, 300)}`);
  }
}

export async function guardarCeldas(corridaId: string, celdas: Celda[]): Promise<void> {
  const { error } = await supabase
    .from('test_masivo_corridas')
    .update({ celdas })
    .eq('id', corridaId);
  if (error) throw error;
}

export interface FilaGenerada {
  execution_id: string;
  has_error: boolean | null;
  /** La imagen vive en el bucket `generadas` como {idCompany}/full/{file_name};
   *  la URL se resuelve con signedUrlsPorExecution. (La tabla NO tiene columna
   *  `url` — asumirla rompía el poll en silencio, bug del 23-ago.) */
  file_name: string | null;
  id: string | number;
  /** Cuándo llegó la imagen: el final del "tardó" de la celda (metricasDeCelda.ts). */
  created_at?: string | null;
  /** `datos_generacion.cost_usd`: gasto de IA de esa imagen. JSON crudo (PostgREST lo
   *  tipa como `Json`): lo convierte a número agruparPorExecution. */
  cost_usd?: unknown;
}

/** Poll batched de la corrida: una query por hasta 100 execution_ids (límite de URL). */
export async function pollFilas(executionIds: string[]): Promise<FilaGenerada[]> {
  // Sin repetidos ANTES de partir en tandas: un id repetido que cae en dos tandas trae
  // sus filas dos veces, y eso duplica imágenes y costos (pasa al comparar una corrida
  // consigo misma) — ADR-2026-10-16-review-185-ids-unicos-en-el-poll.
  return await fetchInChunks<FilaGenerada>([...new Set(executionIds)], (chunk) =>
    supabase
      .from('imagenes_generadas')
      .select('execution_id, has_error, file_name, id, created_at, cost_usd:datos_generacion->cost_usd')
      .in('execution_id', chunk),
  );
}

export interface ProductoRef {
  nombre: string;
  /** URLs de productos_imagenes, principal primero — las referencias que viajan
   *  al proveedor como "Producto N vista K". */
  imagenes: string[];
}

/** Nombre + fotos de referencia de una variante (para "ver el producto" en la
 *  comparación). `productos_imagenes.url` suele ser un FILENAME del bucket
 *  privado `productos` — se firma con la MISMA receta de fetchUserProducts
 *  (`{company}/{variante}/opt/{file}`, fallback a `full/`); usarla cruda no
 *  carga nada (bug v4.5). */
export async function fetchProductoRef(idCompany: string, varianteId: string): Promise<ProductoRef> {
  const [variante, imagenes] = await Promise.all([
    supabase.from('variantes').select('nombre').eq('id', varianteId).single(),
    supabase
      .from('productos_imagenes')
      .select('url, principal, orden')
      .eq('id_company', idCompany)
      .eq('variante_id', varianteId)
      .order('principal', { ascending: false })
      .order('orden'),
  ]);
  const firmadas = await Promise.all(
    ((imagenes.data ?? []) as Array<{ url: string }>).map(async ({ url }) => {
      if (!url) return null;
      if (url.startsWith('http')) return url;
      const { data: opt } = await supabase.storage
        .from('productos')
        .createSignedUrl(`${idCompany}/${varianteId}/opt/${url}`, 3600);
      if (opt?.signedUrl) return opt.signedUrl;
      const { data: full } = await supabase.storage
        .from('productos')
        .createSignedUrl(`${idCompany}/${varianteId}/full/${url}`, 3600);
      return full?.signedUrl ?? null;
    }),
  );
  return {
    nombre: (variante.data as { nombre?: string } | null)?.nombre ?? varianteId.slice(0, 8),
    imagenes: firmadas.filter((x): x is string => !!x),
  };
}

/**
 * Signed URLs (1h) de las filas del poll, para la vista de comparación. **Varias por
 * execution_id**: una celda del modo automático pide N imágenes en UNA llamada y las N
 * filas comparten el execution_id (ADR-2026-08-26-modo-automatico-en-el-test-masivo).
 * Antes esto devolvía una sola URL por execution y la última fila pisaba a las otras.
 *
 * Orden estable por `id` de la fila (el orden en que las escribió el backend), así la
 * tira de miniaturas no se reordena entre polls.
 */
/**
 * URLs firmadas de cada imagen, por execution_id. `porExecution` es el `full/` (lo que se
 * compara con lupa en el visor A | B); `miniaturaDe` lleva de cada `full/` a su `opt/`
 * (800 px) para la grilla, que mostraba el original en tarjetas de 224 px — con 2K por
 * defecto, 2-7 MB por casillero (ADR-2026-10-16-nano-banana-2-1-en-2k-por-defecto). Sin
 * `opt/`, la grilla cae al `full/`.
 */
export async function signedUrlsPorExecution(
  idCompany: string,
  filas: FilaGenerada[],
): Promise<{ porExecution: Map<string, string[]>; miniaturaDe: Map<string, string> }> {
  const ordenadas = [...filas].sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
  const firmar = async (path: string) =>
    (await supabase.storage.from('generadas').createSignedUrl(path, 3600)).data?.signedUrl ?? null;
  const firmadas = await Promise.all(
    ordenadas.map(async (f): Promise<[string, string | null, string | null]> => {
      if (!f.file_name) return [f.execution_id, null, null];
      const [full, opt] = await Promise.all([
        firmar(`${idCompany}/full/${f.file_name}`),
        firmar(`${idCompany}/opt/${f.file_name}`),
      ]);
      return [f.execution_id, full, opt];
    }),
  );
  const porExecution = new Map<string, string[]>();
  const miniaturaDe = new Map<string, string>();
  for (const [execId, url, opt] of firmadas) {
    if (!url) continue;
    porExecution.set(execId, [...(porExecution.get(execId) ?? []), url]);
    if (opt) miniaturaDe.set(url, opt);
  }
  return { porExecution, miniaturaDe };
}

export async function listarEvaluaciones(corridaA: string, corridaB: string): Promise<Evaluacion[]> {
  const { data, error } = await supabase
    .from('test_masivo_evaluaciones')
    .select('corrida_a, corrida_b, config_id, variante_id, ganador, nota')
    .eq('corrida_a', corridaA)
    .eq('corrida_b', corridaB);
  if (error) throw error;
  return (data ?? []) as Evaluacion[];
}

export async function guardarEvaluacion(idCompany: string, ev: Evaluacion): Promise<void> {
  const { error } = await supabase
    .from('test_masivo_evaluaciones')
    .upsert(
      { id_company: idCompany, ...ev },
      { onConflict: 'corrida_a,corrida_b,config_id,variante_id' },
    );
  if (error) throw error;
}
